import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * Lifecycle of an outcome.
 *
 * - `open`: filed, not yet driven by a workflow
 * - `running`: a workflow execution is actively spawning runs
 * - `waiting_human`: blocked on a human answer (a `human` run is pending)
 * - `budget_exhausted`: stopped because `spentUsd` reached `budgetUsd`. Distinct
 *   from `failed` so "we ran out of money" is never read as "the work was
 *   wrong" — it is the one terminal state a top-up can resume from.
 * - `done` / `failed` / `canceled`: terminal
 *
 * Stored as plain varchar, never a pgEnum — adding a status must not require an
 * `ALTER TYPE` and the two-deploy enum-value-isolation dance.
 *
 * @since 1.0.0
 */
export const OutcomeStatus = Schema.Literal(
  "open",
  "running",
  "waiting_human",
  "budget_exhausted",
  "done",
  "failed",
  "canceled",
);
export type OutcomeStatus = Schema.Schema.Type<typeof OutcomeStatus>;

/**
 * Outcome model — text plus a budget, owned by a customer (OL-31).
 *
 * `statement` carries both the desired outcome and its acceptance criteria in
 * the owner's words. `budgetUsd` is the ceiling; `spentUsd` is the accumulator
 * the run costs roll up into. Both are plain `Schema.Number` — the underlying
 * numeric columns use `mode: "number"` so they never surface as strings.
 *
 * `customerId` and `createdByPersonId` are plain strings with no foreign key:
 * `customers` / `people` live in the `public` schema while this table lives in
 * `orcaloops`, and the reference is by id only so the schema can be split out.
 *
 * @since 1.0.0
 */
export class Outcome extends Model.Class<Outcome>("Outcome")({
  id: Model.Generated(Schema.String),

  customerId: Schema.String,

  title: Schema.String,
  statement: Schema.String,

  status: OutcomeStatus,

  budgetUsd: Schema.NullOr(Schema.Number),
  spentUsd: Schema.Number,

  repo: Schema.NullOr(Schema.String),
  workflowExecutionId: Schema.NullOr(Schema.String),
  createdByPersonId: Schema.NullOr(Schema.String),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,

  /** Git ref the work is cut from. `null` = the repo's default branch. */
  ref: Schema.NullOr(Schema.String),
  /** Acceptance criteria stated apart from `statement`, when the owner gave them. */
  acceptance: Schema.NullOr(Schema.String),

  /** Loop governors: concurrent runs allowed, and the orchestrator turn ceiling. */
  maxParallel: Schema.Number,
  maxTurns: Schema.Number,
  /** Turns consumed so far. */
  turn: Schema.Number,

  /**
   * The Linear project this outcome projects onto, once one exists. A
   * projection for display — the engine keys off `orcaloops.*`, never Linear.
   */
  linearProjectId: Schema.NullOr(Schema.String),

  /**
   * The outcome that spawned this one, or `null` for a root outcome (OL-66).
   * Always the PARENT row's own id — there is no payload path to another
   * tenant, because a child inherits the parent's `customerId` verbatim.
   */
  parentOutcomeId: Schema.NullOr(Schema.String),
  /**
   * Depth in the outcome tree: a root is `0`, a child is `parent.depth + 1`.
   * Derived server-side and compared against
   * `ORCALOOPS_NESTED_OUTCOME_MAX_DEPTH`, so a runaway orchestrator cannot fork
   * unboundedly.
   */
  depth: Schema.Number,

  /**
   * The extractor draft that confirmed this execution, or `null` when no draft
   * produced it. Uniquely indexed, so a retried confirm resolves to the same
   * execution instead of buying a second budget.
   */
  draftId: Schema.NullOr(Schema.String),
  /**
   * The loop definition this execution was rendered from (OL-113). `null` is
   * the AD-HOC execution — a permanently supported, first-class path that every
   * row written before this column has, and that the browser's own create path
   * takes.
   */
  loopId: Schema.NullOr(Schema.String),
  /** The exact argument set the `statement` was rendered from. */
  inputs: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.String })),
  /**
   * WHICH version of that loop it rendered from. Together with the rendered
   * `statement` and `inputs`, this makes an invocation fully attributable;
   * refining the loop afterwards changes none of it.
   */
  loopVersion: Schema.NullOr(Schema.Number),
}) {}
